// TODO: Mirror these rules server-side in /onlineBooking to prevent client-side manipulation.
// When playerCount is added to the booking payload, the backend should recompute the
// expected total using these same rules and reject bookings that don't match.

export type DayType = 'WEEKDAY' | 'WEEKEND';
export type RuleId = 'SOLO_PREMIUM' | 'FIVE_PLAYER_DEAL' | 'WEEKEND_BASE' | 'WEEKDAY_BASE';

export interface PriceQuote {
  /** Price per person per hour */
  pricePerPerson: number;
  /** pricePerPerson × playerCount × (durationMinutes / 60) */
  total: number;
  /** Which pricing rule was applied */
  appliedRuleId: RuleId;
  dayType: DayType;
  /** Base day-type rate (₹600 or ₹650) before any player-count overrides */
  basePricePerPerson: number;
}

export function getDayType(date: Date): DayType {
  const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  return day === 0 || day === 6 ? 'WEEKEND' : 'WEEKDAY';
}

/**
 * Quote a price for a booking slot.
 *
 * Rule priority (highest first):
 *  1. playerCount === 1  → Solo premium: ₹800 flat (1 person)
 *  2. playerCount === 5  → 5-player deal: ₹500 / person
 *  3. else               → day-type base rate (₹600 weekday / ₹650 weekend)
 *
 * total = pricePerPerson × playerCount × (durationMinutes / 60)
 *
 * To extend later:
 *  - Peak hours: add `hour?: number` param; insert a PEAK_HOUR rule above the base-rate case.
 *  - Holidays: add `isHoliday?: boolean`; treat as WEEKEND or a custom rate.
 *  - Memberships: accept `membershipDiscount?: number` (0–1); apply after pricePerPerson is resolved.
 */
export function quotePrice({
  date,
  playerCount,
  durationMinutes = 60,
}: {
  date: Date;
  playerCount: number;
  durationMinutes?: number;
}): PriceQuote {
  const dayType = getDayType(date);
  const basePricePerPerson = dayType === 'WEEKEND' ? 650 : 600;
  const hours = durationMinutes / 60;

  let pricePerPerson: number;
  let appliedRuleId: RuleId;

  if (playerCount === 1) {
    pricePerPerson = 800;
    appliedRuleId = 'SOLO_PREMIUM';
  } else if (playerCount === 5) {
    pricePerPerson = 500;
    appliedRuleId = 'FIVE_PLAYER_DEAL';
  } else {
    pricePerPerson = basePricePerPerson;
    appliedRuleId = dayType === 'WEEKEND' ? 'WEEKEND_BASE' : 'WEEKDAY_BASE';
  }

  return {
    pricePerPerson,
    total: pricePerPerson * playerCount * hours,
    appliedRuleId,
    dayType,
    basePricePerPerson,
  };
}
